import { Effect } from 'effect';
import type { BrandConfig } from './types';

/** Hosts that stay on the okraPDF infrastructure even when the UI is ParseGrid. */
const PRESERVED_HOSTS = [
  'api.okrapdf.com',
  'docs.okrapdf.com',
  'clerk.okrapdf.com',
  'res.okrapdf.com',
  'content.okrapdf.com',
  'live.okrapdf.com',
  'embed.okrapdf.com',
  'workflows.okrapdf.com',
  'static.okrapdf.com',
] as const;

function hostnameOf(origin: string): string {
  return new URL(origin).hostname;
}

/**
 * Rewrite user-facing okraPDF name/domain strings for a branded deploy.
 * Infrastructure hosts (API, docs, Clerk) are preserved.
 */
function brandCopyInternal(text: string, brand: BrandConfig): string {
  if (brand.id === 'okrapdf') return text;

  const tokens = PRESERVED_HOSTS.map((host, index) => ({
    host,
    token: `\u0000OKRAHOST${index}\u0000`,
  }));
  let out = text;
  for (const { host, token } of tokens) out = out.replaceAll(host, token);
  out = out.replaceAll('app.okrapdf.com', hostnameOf(brand.origins.app));
  out = out.replaceAll('www.okrapdf.com', brand.hosts.www[0] ?? `www.${brand.domain}`);
  out = out.replaceAll('okraPDF', brand.name);
  out = out.replace(/\bOkra\b/g, brand.name);
  out = out.replaceAll('okrapdf.com', brand.domain);
  for (const { host, token } of tokens) out = out.replaceAll(token, host);
  return out;
}

export function brandCopyEffect(text: string, brand: BrandConfig): Effect.Effect<string> {
  return Effect.sync(() => brandCopyInternal(text, brand));
}

export function brandCopy(text: string, brand: BrandConfig): string {
  return Effect.runSync(brandCopyEffect(text, brand));
}

export function brandCanonicalEffect(url: string, brand: BrandConfig): Effect.Effect<string> {
  return Effect.sync(() => {
    if (brand.id === 'okrapdf') return url;
    try {
      const parsed = new URL(url);
      if (parsed.hostname === 'okrapdf.com' || parsed.hostname === 'www.okrapdf.com') {
        parsed.protocol = 'https:';
        parsed.hostname = hostnameOf(brand.origins.marketing);
        return parsed.toString();
      }
      if (parsed.hostname === 'app.okrapdf.com') {
        parsed.protocol = 'https:';
        parsed.hostname = hostnameOf(brand.origins.app);
        return parsed.toString();
      }
      return url;
    } catch {
      return brandCopyInternal(url, brand);
    }
  });
}

export function brandCanonical(url: string, brand: BrandConfig): string {
  return Effect.runSync(brandCanonicalEffect(url, brand));
}

export const applyBrandToSeoEffect = Effect.fn('web.brand.applySeo')(function* (
  seo: {
    title?: string;
    description?: string;
    canonical?: string;
    jsonLd?: unknown;
  },
  brand: BrandConfig,
) {
  if (brand.id === 'okrapdf') return seo;
  const canonical = seo.canonical
    ? yield* brandCanonicalEffect(seo.canonical, brand)
    : undefined;
  return {
    ...seo,
    ...(seo.title ? { title: brandCopyInternal(seo.title, brand) } : {}),
    ...(seo.description ? { description: brandCopyInternal(seo.description, brand) } : {}),
    ...(canonical ? { canonical } : {}),
    ...(seo.jsonLd !== undefined
      ? { jsonLd: JSON.parse(brandCopyInternal(JSON.stringify(seo.jsonLd), brand)) }
      : {}),
  };
});

export function applyBrandToSeo<T extends {
  title?: string;
  description?: string;
  canonical?: string;
  jsonLd?: unknown;
}>(seo: T, brand: BrandConfig): T {
  return Effect.runSync(applyBrandToSeoEffect(seo, brand)) as T;
}
